import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Send, Mail } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { GithubIcon, LinkedinIcon, TwitterIcon, InstagramIcon, FacebookIcon } from './SocialIcons';
import { useAboutSocials } from '../hooks/useAbout';

const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const SOCIAL_DEFS = [
  { key: 'github',    label: 'GitHub',    fallback: 'https://github.com/rupeshbhatta',      Icon: GithubIcon },
  { key: 'linkedin',  label: 'LinkedIn',  fallback: 'https://linkedin.com/in/rupeshbhatta', Icon: LinkedinIcon },
  { key: 'twitter',   label: 'Twitter',   fallback: 'https://twitter.com/rupeshbhatta',     Icon: TwitterIcon },
  { key: 'instagram', label: 'Instagram', fallback: 'https://instagram.com/rupeshbhatta',   Icon: InstagramIcon },
  { key: 'facebook',  label: 'Facebook',  fallback: 'https://facebook.com/rupeshbhatta',    Icon: FacebookIcon },
];

const EMPTY = { name: '', email: '', subject: '', message: '' };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const cmsSocials = useAboutSocials();

  const socials = SOCIAL_DEFS.map(({ key, label, fallback, Icon }) => ({
    label, Icon,
    href: cmsSocials[key] || fallback,
  }));

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
        from_name:    form.name,
        from_email:   form.email,
        subject:      form.subject,
        message:      form.message,
        reply_to:     form.email,
      }, PUBLIC_KEY);
      setStatus('success');
      setForm(EMPTY);
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" ref={ref} className={`section${inView ? ' visible' : ''}`}>
      <div className="container">
        <header className="section-header">
          <p className="section-label">contact</p>
          <h2 className="section-title">Get In Touch</h2>
          <div className="section-line" />
        </header>

        <div className="contact-grid">
          <div className="contact-info">
            <h3>Let's build something together</h3>
            <p>
              Whether you have a project in mind, want to collaborate, or just want
              to say hi — my inbox is always open.
            </p>

            <div className="contact-detail">
              <Mail size={16} />
              <a href="mailto:rupesh.bhatta1234@gmail.com">
                rupesh.bhatta1234@gmail.com
              </a>
            </div>

            <div className="contact-socials">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="social-icon-btn"
                  aria-label={label}
                  title={label}
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cf-name">Name</label>
                <input
                  id="cf-name"
                  className="form-input"
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={set('name')}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cf-email">Email</label>
                <input
                  id="cf-email"
                  className="form-input"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set('email')}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="cf-subject">Subject</label>
              <input
                id="cf-subject"
                className="form-input"
                type="text"
                placeholder="What's this about?"
                value={form.subject}
                onChange={set('subject')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cf-msg">Message</label>
              <textarea
                id="cf-msg"
                className="form-textarea"
                placeholder="Tell me more..."
                value={form.message}
                onChange={set('message')}
                required
              />
            </div>

            {status === 'success' && (
              <p className="form-status success">Message sent! I'll get back to you soon.</p>
            )}
            {status === 'error' && (
              <p className="form-status error">
                {!SERVICE_ID
                  ? 'EmailJS not configured. Check .env.local and DOCUMENTATION.md.'
                  : 'Something went wrong. Please try emailing directly.'}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={status === 'sending'}
              style={{ alignSelf: 'flex-start' }}
            >
              {status === 'sending' ? 'Sending…' : <><Send size={15} /> Send Message</>}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
